import { memo, useState, useCallback, useEffect, useMemo } from 'react';
import { useBottomModal, IcTxtArrowSmallThin, ButtonBoxDouble } from '@defign/ui';
import { AgreementItemCode } from '@mydata/utils/agreement';
import { addDays } from 'date-fns';
import { Cookies } from 'react-cookie';
import { IconCheck } from '@defign/asset';
import { sendAULog, Action } from '@financial/common-logs';
import classNames from 'classnames/bind';
import { SvgUniqueID } from '@naverpay/svg-manager';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

// src/OptionalAgreementLayer/index.tsx

// src/constants/routes.ts
var API_ROUTES = {
  CONNECT: {
    SCHEME_DATA: "/mydata/api/connect/schemeData",
    AGREEMENT: "/mydata/api/connect/agreement",
    AGREEMENT_THIRD_PARTY: "/mydata/api/connect/agreement/third-party"
  }
};
var PAGE_ROUTES = {
  CONNECT: {
    AGREEMENT_PROVISION_DETAIL: "/mydata/connect/agreement/provision/detail"
  }
};

// src/constants/responseStoreKeys.ts
var RESPONSE_STORE_KEYS = {
  CONNECT: {
    SCHEME_DATA: (scheme) => API_ROUTES.CONNECT.SCHEME_DATA + "_" + scheme,
    AGREEMENT: (enabled = true) => API_ROUTES.CONNECT.AGREEMENT + "_" + enabled
  }
};

// src/stores/ResponseStore.ts
var ResponseStore = class _ResponseStore {
  static _responseStore;
  _domain = "";
  _store = {};
  _fetchingMap = {};
  static getSingleton() {
    if (!this._responseStore) {
      this._responseStore = new _ResponseStore();
    }
    return this._responseStore;
  }
  getDomain() {
    return this._domain;
  }
  setDomain(domain) {
    this._domain = domain;
  }
  getResponse(key) {
    return this._store[key];
  }
  setResponse(key, value) {
    this._store[key] = value;
  }
  isFetching(key) {
    return this._fetchingMap[key];
  }
  setFetching(key, status) {
    this._fetchingMap[key] = status;
  }
};
var ResponseStore_default = ResponseStore;

// src/hooks/useResponseStore.ts
function useResponseStore({ key, enable = true, init = void 0, fn, cache = true }) {
  const [response, setResponse] = useState(init);
  useEffect(() => {
    if (!enable) {
      return;
    }
    const store = ResponseStore_default.getSingleton();
    const storeResponse = store.getResponse(key);
    if (storeResponse && cache) {
      setResponse(storeResponse);
    } else {
      if (store.isFetching(key) && cache) {
        return;
      }
      store.setFetching(key, true);
      fn()?.then(async (res) => {
        if (res) {
          const result = await res.json();
          setResponse(result);
          store.setResponse(key, result);
        }
      }).finally(() => {
        store.setFetching(key, false);
      });
    }
  }, [key, fn, cache, enable]);
  return response;
}
var useResponseStore_default = useResponseStore;
var ImageMyAssetPoint = memo(({ width = "100%", height = "100%", id, viewBox = "0 0 42 42" }) => /* @__PURE__ */ jsx(SvgUniqueID, { id, children: /* @__PURE__ */ jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width, height, viewBox, fill: "none", children: [
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M20.193 6.57038C12.2522 5.87043 5.832 11.9769 5.88028 20.2072C5.90441 28.4376 12.3729 35.6785 20.3136 36.3784C28.2544 37.0784 34.6746 30.9719 34.6263 22.7415C34.6022 14.5111 28.1337 7.27032 20.193 6.57038Z",
      fill: "#FFE62D"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M22.4376 5.0976C19.2758 4.80797 16.3311 5.62859 13.9658 7.22157L11.9142 8.54905C14.2554 7.02848 17.1035 6.28027 20.1929 6.54576C28.1337 7.24571 34.6021 14.4865 34.6263 22.7169C34.6504 27.7372 32.2851 31.9369 28.6405 34.3264L30.9576 32.8058C34.5539 30.4163 36.8951 26.2166 36.8709 21.2446C36.8468 13.0384 30.3783 5.79755 22.4376 5.0976Z",
      fill: "url(#paint0_linear_4204_30162)"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M28.2061 13.6664C25.9373 11.1321 22.9686 9.58741 19.8068 9.32191C19.4689 9.29777 19.1551 9.27364 18.8172 9.27364C16.0657 9.27364 13.5073 10.3115 11.6005 12.2182C9.57309 14.2457 8.46282 17.0937 8.48696 20.2073C8.51109 23.5139 9.76618 26.7481 12.0591 29.3066C14.3279 31.8408 17.2966 33.3856 20.4584 33.6511C20.7964 33.6752 21.1101 33.6993 21.448 33.6993C24.1995 33.6993 26.758 32.6615 28.6647 30.7547C30.6921 28.7273 31.8024 25.8792 31.7782 22.7657C31.7541 19.4349 30.499 16.2007 28.2061 13.6664Z",
      fill: "url(#paint1_linear_4204_30162)"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M26.5648 19.7239V14.9691L23.8857 14.7036V22.572L18.5999 14.1726L15.7519 13.883V18.6378L12.6866 18.3481V21.0514L15.7519 21.341V26.0958L18.431 26.3613V18.493L23.7168 26.8923L26.5648 27.1819V22.4271L29.6301 22.7168V20.0377L26.5648 19.7239Z",
      fill: "#EFD500"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M25.4546 20.8351V16.0803L22.7513 15.8148V23.6831L17.4655 15.2838L14.6175 15.0183V19.7731L11.5764 19.4593V22.1626L14.6175 22.4763V27.207L17.3207 27.4966V19.6283L22.6065 28.0035L25.4546 28.2931V23.5383L28.4957 23.8521V21.1489L25.4546 20.8351Z",
      fill: "#605000"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M22.4376 5.0976C19.2758 4.80797 16.3311 5.62859 13.9658 7.22157L11.9142 8.54905C14.2554 7.02848 17.1035 6.28027 20.1929 6.54576C28.1337 7.24571 34.6021 14.4865 34.6263 22.7169C34.6504 27.7372 32.2851 31.9369 28.6405 34.3264L30.9576 32.8058C34.5539 30.4163 36.8951 26.2166 36.8709 21.2446C36.8468 13.0384 30.3783 5.79755 22.4376 5.0976Z",
      stroke: "black",
      strokeWidth: "0.122709",
      strokeMiterlimit: "10",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M20.193 6.57038C12.2522 5.87043 5.832 11.9769 5.88028 20.2072C5.90441 28.4376 12.3729 35.6785 20.3136 36.3784C28.2544 37.0783 34.6746 30.9719 34.6263 22.7415C34.6022 14.5111 28.1337 7.27032 20.193 6.57038Z",
      stroke: "black",
      strokeWidth: "0.122709",
      strokeMiterlimit: "10",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M12.059 29.3066C14.3278 31.8409 17.2965 33.3856 20.4584 33.6511C20.7963 33.6752 21.11 33.6993 21.4479 33.6993C24.1994 33.6993 26.7579 32.6615 28.6646 30.7547C30.6921 28.7273 31.8023 25.8792 31.7782 22.7657C31.754 19.4591 30.499 16.2248 28.206 13.6664",
      stroke: "url(#paint2_linear_4204_30162)",
      strokeWidth: "0.122709",
      strokeMiterlimit: "10",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      opacity: "0.35",
      d: "M20.0964 34.4714C20.8929 34.6886 21.7135 34.8334 22.5583 34.9059C25.1649 35.1231 27.6027 34.6404 29.7025 33.5542",
      stroke: "url(#paint3_linear_4204_30162)",
      strokeWidth: "0.122709",
      strokeMiterlimit: "10",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ),
  /* @__PURE__ */ jsx(
    "path",
    {
      opacity: "0.35",
      d: "M13.0484 7.92176C10.0072 10.3836 8.10048 14.2454 8.12462 18.7588",
      stroke: "url(#paint4_linear_4204_30162)",
      strokeWidth: "0.122709",
      strokeMiterlimit: "10",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ),
  /* @__PURE__ */ jsxs("defs", { children: [
    /* @__PURE__ */ jsxs(
      "linearGradient",
      {
        id: "paint0_linear_4204_30162",
        x1: "16.6617",
        y1: "10.7674",
        x2: "39.8473",
        y2: "29.7809",
        gradientUnits: "userSpaceOnUse",
        children: [
          /* @__PURE__ */ jsx("stop", { offset: "0.1", stopColor: "#FFE62D" }),
          /* @__PURE__ */ jsx("stop", { offset: "0.2906", stopColor: "#FDE029" }),
          /* @__PURE__ */ jsx("stop", { offset: "0.5422", stopColor: "#F8D01F" }),
          /* @__PURE__ */ jsx("stop", { offset: "0.8267", stopColor: "#EFB40D" }),
          /* @__PURE__ */ jsx("stop", { offset: "1", stopColor: "#E8A000" })
        ]
      }
    ),
    /* @__PURE__ */ jsxs(
      "linearGradient",
      {
        id: "paint1_linear_4204_30162",
        x1: "13.2934",
        y1: "10.9351",
        x2: "28.4414",
        y2: "34.2665",
        gradientUnits: "userSpaceOnUse",
        children: [
          /* @__PURE__ */ jsx("stop", { stopColor: "#FFFFDD" }),
          /* @__PURE__ */ jsx("stop", { offset: "1", stopColor: "#FFE62D" })
        ]
      }
    ),
    /* @__PURE__ */ jsxs(
      "linearGradient",
      {
        id: "paint2_linear_4204_30162",
        x1: "21.919",
        y1: "33.7553",
        x2: "21.919",
        y2: "13.5943",
        gradientUnits: "userSpaceOnUse",
        children: [
          /* @__PURE__ */ jsx("stop", {}),
          /* @__PURE__ */ jsx("stop", { offset: "1", stopOpacity: "0" })
        ]
      }
    ),
    /* @__PURE__ */ jsxs(
      "linearGradient",
      {
        id: "paint3_linear_4204_30162",
        x1: "29.7715",
        y1: "34.2677",
        x2: "20.0405",
        y2: "34.2677",
        gradientUnits: "userSpaceOnUse",
        children: [
          /* @__PURE__ */ jsx("stop", {}),
          /* @__PURE__ */ jsx("stop", { offset: "0.95", stopOpacity: "0" })
        ]
      }
    ),
    /* @__PURE__ */ jsxs(
      "linearGradient",
      {
        id: "paint4_linear_4204_30162",
        x1: "10.5759",
        y1: "7.86118",
        x2: "10.5759",
        y2: "18.8138",
        gradientUnits: "userSpaceOnUse",
        children: [
          /* @__PURE__ */ jsx("stop", {}),
          /* @__PURE__ */ jsx("stop", { offset: "0.95", stopOpacity: "0" })
        ]
      }
    )
  ] })
] }) }));
var ImageMyAssetPoint_default = ImageMyAssetPoint;

// src/errors/PopupError.ts
var PopupError = class extends Error {
  constructor(params) {
    super(params.message);
    this.params = params;
  }
  name = "PopupError";
  isPopupError = true;
  get title() {
    return this.params.title;
  }
};

// src/services/fetch.ts
var FetchError = class extends Error {
  name = "FetchError";
  _response;
  _responseJson;
  constructor(response, message) {
    super(message);
    this._response = response;
  }
  get response() {
    return this._response;
  }
  async getJson() {
    try {
      this._responseJson = await this._response.json();
      return this._responseJson;
    } catch {
      return this._responseJson;
    }
  }
};
var getFetchResponse = async ({ path, method, body }) => {
  const baseURL = ResponseStore_default.getSingleton().getDomain();
  const response = await fetch(baseURL + path, {
    headers: { "Content-Type": "application/json" },
    method,
    ...body && { body: JSON.stringify(body) }
  });
  if (!response.ok) {
    throw new FetchError(response.clone());
  }
  return response;
};

// src/services/agreement.ts
async function getMydataAgreement() {
  const result = await getFetchResponse({ path: API_ROUTES.CONNECT.AGREEMENT, method: "GET" });
  return result;
}
async function patchMydataOptionalAgreement({ itemCodes, checked }) {
  try {
    return getFetchResponse({
      path: API_ROUTES.CONNECT.AGREEMENT,
      method: "PATCH",
      body: { itemCodes, checked }
    });
  } catch (error) {
    throw new PopupError({
      title: "\uC57D\uAD00 \uB3D9\uC758\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4.",
      message: "\uC7A0\uC2DC \uD6C4 \uB2E4\uC2DC \uC2DC\uB3C4\uD574 \uC8FC\uC138\uC694."
    });
  }
}

// src/OptionalAgreementLayer/components/Layer.module.scss
var Layer_module_default = {
  "article": "_article_bx6ov_3",
  "type-connect": "_type-connect_bx6ov_7",
  "banner-area": "_banner-area_bx6ov_7",
  "graphic": "_graphic_bx6ov_11",
  "description": "_description_bx6ov_14",
  "agreement-list": "_agreement-list_bx6ov_17",
  "layer-title": "_layer-title_bx6ov_21",
  "text": "_text_bx6ov_49",
  "intro": "_intro_bx6ov_54",
  "item": "_item_bx6ov_66",
  "label": "_label_bx6ov_73",
  "icon-check": "_icon-check_bx6ov_83",
  "link": "_link_bx6ov_89",
  "icon-arrow": "_icon-arrow_bx6ov_95"
};
var cx = classNames.bind(Layer_module_default);
var cookies = new Cookies();
var setSkipCookie = (key, expires) => cookies.set(key, true, { expires, path: "/" });
var OAL_SERVICE_TYPE = {
  BANK: "oal_bank",
  CARD: "oal_card",
  EFIN: "oal_efin",
  INVEST: "oal_invest",
  INSU: "oal_insu",
  PENSION: "oal_pension",
  LOAN: "oal_loan",
  CONNECT: "oal_connect"
};
var OAL_TITLE = {
  [AgreementItemCode.OA1]: "\uB9DE\uCDA4\uD615 \uC11C\uBE44\uC2A4 \uC81C\uACF5 \uAC1C\uC778\uC815\uBCF4 \uCC98\uB9AC \uB3D9\uC758",
  [AgreementItemCode.OB1]: "\uB9C8\uC774\uB370\uC774\uD130 \uB9C8\uCF00\uD305 \uD65C\uC6A9 \uB3D9\uC758"
};
var Layer = (props) => {
  const {
    serviceType,
    onClose,
    onSuccess,
    onFinish,
    onCancel,
    auBaseInfo,
    useNewColor,
    optionalAgreements,
    itemList: originItemList,
    isShowLayer,
    setIsShowLayer
  } = props;
  const [itemList, setItemList] = useState(JSON.parse(JSON.stringify(originItemList)));
  const isConnectType = serviceType === OAL_SERVICE_TYPE.CONNECT;
  const isInjected = !!optionalAgreements;
  const name = isConnectType ? props.name : void 0;
  const isAllDisagree = useMemo(() => itemList.every(({ checked }) => !checked), [itemList]);
  const { image, text, description, auImpressionCode, auClickCode } = useMemo(
    () => isConnectType ? {
      image: /* @__PURE__ */ jsx(ImageMyAssetPoint_default, { width: 50, height: 50 }),
      text: "\uB124\uC774\uBC84\uD398\uC774 \uD3EC\uC778\uD2B8 \uD61C\uD0DD \uCD94\uCC9C",
      description: "\uB193\uCE58\uACE0 \uC788\uB294 \uD3EC\uC778\uD2B8 \uB2E4 \uCC59\uAE30\uAE30",
      auImpressionCode: "view_benefit",
      auClickCode: "click_benefit"
    } : {
      image: /* @__PURE__ */ jsx(ImageMyAssetPoint_default, { width: 42, height: 42 }),
      description: "\uB193\uCE5C \uB124\uC774\uBC84\uD398\uC774 \uD3EC\uC778\uD2B8\uAC00 \uC788\uC5B4\uC694",
      auImpressionCode: "oa_popup_point",
      auClickCode: "oa_popup_point"
    },
    [isConnectType]
  );
  const handleChangeItem = useCallback((e) => {
    const { checked, value } = e.currentTarget;
    setItemList(
      (prevItemList) => prevItemList.map((data) => data.item.code === value ? { ...data, checked } : data)
    );
  }, []);
  const handleNextClick = useCallback(async () => {
    auBaseInfo && sendAULog({ ...auBaseInfo, code: auClickCode, element: "disagree" }, Action.Click);
    const expires = isInjected ? addDays(/* @__PURE__ */ new Date(), 7) : /* @__PURE__ */ new Date("9999-12-31T23:59:59");
    Object.values(OAL_SERVICE_TYPE).filter((type) => type !== OAL_SERVICE_TYPE.CONNECT).forEach((keyType) => setSkipCookie(keyType, expires));
    setIsShowLayer(false);
    onClose?.();
    await onCancel?.();
  }, [auBaseInfo, auClickCode, isInjected, onCancel, onClose, setIsShowLayer]);
  const handleAgreeClick = useCallback(async () => {
    auBaseInfo && sendAULog({ ...auBaseInfo, code: auClickCode, element: "agree" }, Action.Click);
    setIsShowLayer(false);
    onClose?.();
    await onSuccess?.();
    const \uB3D9\uC758\uD560_\uC57D\uAD00_\uBAA9\uB85D = (isAllDisagree ? itemList.map((data) => ({ ...data, checked: true })) : itemList.filter(({ checked }) => checked)).map(({ item: { code } }) => code);
    await Promise.allSettled([
      patchMydataOptionalAgreement({ itemCodes: \uB3D9\uC758\uD560_\uC57D\uAD00_\uBAA9\uB85D, checked: true }),
      // 동의한 경우에도 쿠키 설정 https://oss.fin.navercorp.com/Product-Planning/Myasset-Plan/issues/25#issuecomment-1004070
      !isInjected && setSkipCookie(serviceType, addDays(/* @__PURE__ */ new Date(), 1))
    ]);
    await onFinish?.();
  }, [
    auBaseInfo,
    auClickCode,
    onClose,
    onSuccess,
    isAllDisagree,
    itemList,
    isInjected,
    serviceType,
    onFinish,
    setIsShowLayer
  ]);
  useEffect(() => {
    isShowLayer && auBaseInfo && sendAULog({ ...auBaseInfo, code: auImpressionCode }, Action.View);
  }, [auBaseInfo, auImpressionCode, isShowLayer, serviceType]);
  return /* @__PURE__ */ jsxs("div", { className: cx({ article: true, "type-connect": isConnectType }), children: [
    /* @__PURE__ */ jsx("h3", { className: cx("layer-title"), children: name ? /* @__PURE__ */ jsxs(Fragment, { children: [
      name,
      "\uB2D8\uAED8 \uB3C4\uC6C0 \uB418\uB294",
      /* @__PURE__ */ jsx("br", {}),
      "\uAE08\uC735 \uC815\uBCF4\uB3C4 \uC54C\uB824\uB4DC\uB9B4\uAC8C\uC694"
    ] }) : "\uB0B4\uAC8C \uC720\uC6A9\uD55C \uAE08\uC735 \uC815\uBCF4 \uC54C\uB824\uB4DC\uB9B4\uAC8C\uC694" }),
    /* @__PURE__ */ jsxs("div", { className: cx("banner-area"), children: [
      /* @__PURE__ */ jsx("div", { className: cx("graphic"), children: image }),
      /* @__PURE__ */ jsx("p", { className: cx("description"), children: text ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("strong", { className: cx("text"), children: text }),
        /* @__PURE__ */ jsx("span", { className: cx("intro"), children: description })
      ] }) : description })
    ] }),
    /* @__PURE__ */ jsx("ul", { className: cx("agreement-list"), children: itemList.map(({ checked, item: { code, title } }) => {
      return /* @__PURE__ */ jsxs("li", { className: cx("item"), children: [
        /* @__PURE__ */ jsx(
          "input",
          {
            type: "checkbox",
            id: `agree-${code}`,
            value: code,
            checked,
            className: "hidden",
            onChange: handleChangeItem
          }
        ),
        /* @__PURE__ */ jsxs("label", { htmlFor: `agree-${code}`, className: cx("label"), children: [
          /* @__PURE__ */ jsx("span", { className: cx("icon-check"), children: /* @__PURE__ */ jsx(IconCheck, { width: 14, height: 11, fill: checked ? "#09aa5c" : "#bbbbbd" }) }),
          OAL_TITLE[code] || title
        ] }),
        /* @__PURE__ */ jsx(
          "a",
          {
            className: cx("link"),
            href: `${PAGE_ROUTES.CONNECT.AGREEMENT_PROVISION_DETAIL}?code=${code}`,
            children: /* @__PURE__ */ jsx("span", { className: cx("icon-arrow"), children: /* @__PURE__ */ jsx(IcTxtArrowSmallThin, { width: 6, height: 10, fill: "#c8cacc" }) })
          }
        )
      ] }, code);
    }) }),
    /* @__PURE__ */ jsx(
      ButtonBoxDouble,
      {
        size: "large",
        ratio: "46",
        leftButton: {
          text: "\uB2E4\uC74C\uC5D0",
          variant: "secondary",
          color: useNewColor ? "gray" : "green",
          onClick: handleNextClick
        },
        rightButton: {
          text: isAllDisagree ? "\uBAA8\uB450 \uB3D9\uC758\uD560\uAC8C\uC694" : "\uB124, \uB3D9\uC758\uD560\uAC8C\uC694",
          variant: "primary",
          color: useNewColor ? "npayGreen" : "green",
          onClick: handleAgreeClick
        }
      }
    )
  ] });
};
var cookies2 = new Cookies();
var setSkipCookie2 = (key, expires) => cookies2.set(key, true, { expires, path: "/" });
var OAL_SERVICE_TYPE2 = {
  BANK: "oal_bank",
  CARD: "oal_card",
  EFIN: "oal_efin",
  INVEST: "oal_invest",
  INSU: "oal_insu",
  PENSION: "oal_pension",
  LOAN: "oal_loan",
  CONNECT: "oal_connect"
};
var oalTargetCodes = [AgreementItemCode.OA1, AgreementItemCode.OB1];
var OptionalAgreementLayer = (props) => {
  const { serviceType, isShow, onClose, optionalAgreements } = props;
  const isInjected = !!optionalAgreements;
  const { open, close } = useBottomModal();
  const [isShowLayer, setIsShowLayer] = useState(null);
  const agreementInfo = useResponseStore_default({
    key: RESPONSE_STORE_KEYS.CONNECT.AGREEMENT(!isInjected),
    enable: !isInjected,
    fn: () => getMydataAgreement()
  });
  const handleClose = useCallback(() => {
    !isInjected && setSkipCookie2(serviceType, addDays(/* @__PURE__ */ new Date(), 1));
    setIsShowLayer(false);
    onClose?.();
  }, [isInjected, onClose, serviceType]);
  useEffect(() => {
    setIsShowLayer(isShow);
  }, [isShow, setIsShowLayer]);
  const itemList = useMemo(() => {
    const isAgreed = agreementInfo?.agreed;
    const hasCookies = cookies2.get(serviceType);
    const shouldShowItems = isShow && (isInjected || !hasCookies && isAgreed);
    if (!shouldShowItems) {
      return [];
    }
    const agreementList = isInjected ? optionalAgreements : agreementInfo?.items || [];
    return agreementList.filter(({ checked }) => !checked).filter(({ item: { code } }) => oalTargetCodes.includes(code));
  }, [isShow, isInjected, optionalAgreements, agreementInfo, serviceType]);
  useEffect(() => {
    if (isShowLayer && itemList.length > 0) {
      open({
        onClickDimmedLayer: handleClose,
        children: /* @__PURE__ */ jsx(Layer, { ...props, itemList, isShowLayer, setIsShowLayer })
      });
    } else {
      close();
    }
    return () => {
      close();
    };
  }, [open, close, isShowLayer, handleClose, props, itemList, setIsShowLayer]);
  return null;
};

export { OAL_SERVICE_TYPE2 as OAL_SERVICE_TYPE, OptionalAgreementLayer };
//# sourceMappingURL=out.js.map
//# sourceMappingURL=index.mjs.map